import { defineStore } from 'pinia';
import type { BlogItem, BlogItemComponent } from '~/units/blogs/types';
import { useDataFromState } from '~/composables';
import { RepoStates } from '~/units/core/types';
import { useCoreStore } from '~/units/core/store';
import type { Component } from '~/types';
import { NOT_FOUND_IMAGE } from '~/constants/ui.constants';
import { getIdOfYoutubeVideo } from '~/helpers';

export const mapBlog = (item: BlogItem): BlogItemComponent => {
  const mediaType = item && item.article && item.article.media_type ? item.article.media_type : 'image';

  const videoLink = item && item.article && item.article.video_link ? item.article.video_link : '';

  const youtubeImage =
    mediaType === 'video' ? `https://i3.ytimg.com/vi/${getIdOfYoutubeVideo(videoLink)}/hqdefault.jpg` : '';

  const image =
    mediaType === 'video'
      ? youtubeImage
      : item && item.article && item.article.image
        ? `https://lifesycle-cdn.s3.eu-west-2.amazonaws.com/${item.article.image.small}`
        : NOT_FOUND_IMAGE;

  return {
    article_id: item.article && item.article.article_id ? item.article.article_id : null,
    original: item,
    title: item && item.article && item.article.title ? item.article.title : '',
    image,
    largeImage:
      item && item.article && item.article.image
        ? `https://lifesycle-cdn.s3.eu-west-2.amazonaws.com/${item.article.image.large}`
        : NOT_FOUND_IMAGE,
    url: item && item.article && item.article.slug ? item.article.slug : '',
    description: item && item.article && item.article.introduction_text ? item.article.introduction_text : '',
    video_link: videoLink,
    media_type: mediaType,
    content: item && item.article && item.article.content ? item.article.content : '',
  };
};

export const useBlogsStore = defineStore('blogs', {
  state: () => {
    return {};
  },
  actions: {
    async fetchBlogsInPage(component: Component) {
      await callOnce(async () => {
        const nuxtApp = useNuxtApp();

        await nuxtApp.runWithContext(async () => {
          const route = useRoute();
          const keyword = route.query.q ? route.query.q.toString() : null;

          await fetchBlogsManager(component, keyword);
        });
      });
    },

    async fetchSingleBlog(component: Component) {
      await callOnce(async () => {
        try {
          const nuxtApp = useNuxtApp();

          const route = useRoute();
          const slug = route.params.blog_id as string;

          if (slug) {
            await nuxtApp.runWithContext(async () => {
              await fetchBlogManager(component, slug);
            });

            nuxtApp.runWithContext(() => {
              if (process.server) {
                const { data } = useDataFromState(RepoStates.SINGLE_BLOG_REPO);
                if (data && data[0] && data[0].data && data[0].data.article && data[0].data.article.title) {
                  const { article } = data[0].data;
                  const coreStore = useCoreStore();
                  const title = article.title;
                  const introductionText = article.introduction_text;
                  const image = article.image ? article.image.large : '';

                  coreStore.updateMetaTagsGroup({
                    title,
                    seo_meta_title: title,
                    og_meta_title: title,
                    seo_meta_description: introductionText || '',
                    og_meta_description: introductionText || '',
                    og_meta_image: `https://lifesycle-cdn.s3.eu-west-2.amazonaws.com/${image}`,
                  });
                } else {
                  navigateTo({
                    path: '/not-found',
                    query: {
                      path: route.path,
                    },
                  });
                }
              }
            });
          }
        } catch (e) {
          // log!!
        }
      });
    },

    async searchBlogs(keyword: string) {
      const router = useRouter();
      if (keyword && keyword.toString().trim()) {
        await router.push({
          query: {
            q: keyword,
          },
        });
      } else {
        await router.push({});
      }
    },
  },
});
